/**
 * alertshub#8 (Jay 2026-10-12: new alerts and client messages get a sound and a desktop push) -
 * desktop notifications and an alert chime in the EHR, while it's open in a browser tab.
 *
 *  - Desktop push needs the practice's Data & Settings → Notifications → Desktop push on, and this
 *    browser's permission (asked from that same row). It shows when the EHR isn't the tab in front;
 *    in front, the bell and the in-app alert already say so.
 *  - The chime is per browser (Data & Settings → Notifications → Sound), on unless turned off.
 *  - Nothing here carries a message's text: a notice says what arrived, never what it says.
 */

import { api } from './api.js';

export type PushPermission = 'unsupported' | 'default' | 'granted' | 'denied';

let pushSetting: Promise<boolean> | null = null;
/** The practice's Desktop push switch (any signed-in user may read it), fetched once per page. */
export function practicePush(): Promise<boolean> {
  pushSetting ??= api
    .get<{ push?: boolean }>('/practice/notifications')
    .then((r) => Boolean(r.push))
    .catch(() => false);
  return pushSetting;
}
/** Data & Settings saved the switch: read it again next time. */
export function resetPracticePush(): void {
  pushSetting = null;
}

export function pushPermission(): PushPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission as PushPermission;
}

/** Ask the browser (it only asks once; after a block, its own site settings change it). */
export async function requestPushPermission(): Promise<PushPermission> {
  if (pushPermission() === 'unsupported') return 'unsupported';
  try {
    return (await Notification.requestPermission()) as PushPermission;
  } catch {
    return pushPermission();
  }
}

const SOUND_KEY = 'credify.alertSound';

/** The alert chime for this browser: on unless turned off. */
export function soundOn(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setSoundOn(on: boolean): void {
  try {
    localStorage.setItem(SOUND_KEY, on ? 'on' : 'off');
  } catch {
    /* private mode: the chime just stays on */
  }
}

let audio: AudioContext | null = null;

/** A short two-note chime (Web Audio, no file). Silent if the browser won't play yet. */
export function playChime(): void {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    audio ??= new Ctx();
    const at = audio.currentTime;
    for (const [i, hz] of [880, 660].entries()) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = 'sine';
      osc.frequency.value = hz;
      const start = at + i * 0.14;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.08, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.13);
      osc.connect(gain).connect(audio.destination);
      osc.start(start);
      osc.stop(start + 0.14);
    }
  } catch {
    /* no audio: the bell still shows it */
  }
}

export interface DesktopAlert {
  title: string;
  body?: string;
  /** Same tag = the browser replaces the earlier notice instead of stacking another. */
  tag?: string;
}

/** True when the EHR isn't what the person is looking at (another tab, app or a minimized window). */
export const inBackground = () =>
  document.visibilityState !== 'visible' || (document.hasFocus ? !document.hasFocus() : false);

/**
 * Something new arrived: chime (if on) and, when the practice allows desktop push, this browser
 * granted it and the EHR is in the background, show a desktop notice for each (at most 3).
 */
export function announce(items: DesktopAlert[], practicePush: boolean): void {
  if (!items.length) return;
  if (soundOn()) playChime();
  if (!practicePush || pushPermission() !== 'granted' || !inBackground()) return;
  for (const it of items.slice(0, 3)) {
    try {
      const n = new Notification(it.title, { body: it.body, tag: it.tag });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {
      /* some browsers only allow notices from a service worker: the bell still has it */
    }
  }
}

/**
 * The bell's new unread notices since the last look (none on the first look). New client messages
 * are left out: the Messages unread check announces those itself, so one message chimes once.
 */
export function newSince<T extends { id: string; type?: string }>(
  seen: Set<string> | null,
  items: T[],
): T[] {
  return seen ? items.filter((n) => !seen.has(n.id) && n.type !== 'MSG_CLIENT_NEW') : [];
}
